import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";
import DataTable from "../components/DataTable";
import SeverityBadge from "../components/SeverityBadge";
import { Button } from "../components/ui/button";
import { useAuth } from "../contexts/AuthContext";
import { formatIDR, formatPercent, formatDate } from "../lib/format";
import { Plus } from "lucide-react";
import FacilityFormDialog from "../components/forms/FacilityFormDialog";

export default function Facilities() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const { user } = useAuth();
  const navigate = useNavigate();
  const canWrite = user && (user.role === "admin" || user.role === "credit_officer");

  const load = () => {
    setLoading(true);
    api.get("/facilities").then((r) => setData(r.data)).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps -- load once on mount

  return (
    <div className="p-8 max-w-[1600px] mx-auto" data-testid="facilities-page">
      <PageHeader
        title="Fasilitas Kredit"
        subtitle={`${data.length} fasilitas dalam portofolio`}
        action={canWrite && (
          <Button data-testid="add-facility-button" onClick={() => { setEditing(null); setDialogOpen(true); }}>
            <Plus className="h-4 w-4 mr-1" /> Tambah Fasilitas
          </Button>
        )}
      />

      {loading ? <div className="text-sm text-muted-foreground">Memuat…</div> : (
        <DataTable
          testId="facilities-table"
          searchKeys={["facility_id", "borrower_id", "borrower_name", "facility_type"]}
          columns={[
            { header: "Facility ID", cell: (r) => (
              <button className="mono font-medium hover:underline" onClick={() => navigate(`/facilities/${r.facility_id}`)} data-testid={`facility-${r.facility_id}-link`}>
                {r.facility_id}
              </button>
            ) },
            { header: "Borrower", cell: (r) => (
              <div>
                <div className="font-medium text-xs">{r.borrower_name}</div>
                <div className="mono text-[10px] text-muted-foreground">{r.borrower_id}</div>
              </div>
            ) },
            { header: "Tipe", key: "facility_type" },
            { header: "Approved", cell: (r) => formatIDR(r.approved_limit, { compact: true }), align: "right" },
            { header: "Utilized", cell: (r) => formatIDR(r.utilized_limit, { compact: true }), align: "right" },
            { header: "Available", cell: (r) => formatIDR(r.available_limit, { compact: true }), align: "right" },
            { header: "Util %", cell: (r) => {
              const ratio = r.utilization_ratio || 0;
              return <span className={`mono ${ratio > 1 ? "text-[#D0021B] font-bold" : ratio >= 0.9 ? "text-[#F5A623] font-bold" : ""}`}>{formatPercent(ratio)}</span>;
            }, align: "right" },
            { header: "Jatuh Tempo", cell: (r) => <span className="mono text-xs">{formatDate(r.maturity_date)}</span> },
            { header: "Status", cell: (r) => {
              if (r.utilization_ratio > 1) {
                return r.allow_excess
                  ? <SeverityBadge severity="warning">EXCESS ACK</SeverityBadge>
                  : <SeverityBadge severity="critical">BREACH</SeverityBadge>;
              }
              return r.utilization_ratio >= 0.9
                ? <SeverityBadge severity="warning" />
                : <span className="text-xs text-muted-foreground">OK</span>;
            } },
            ...(canWrite ? [{ header: "Aksi", cell: (r) => (
              <Button size="sm" variant="ghost" onClick={() => { setEditing(r); setDialogOpen(true); }} data-testid={`edit-facility-${r.facility_id}`}>Edit</Button>
            ) }] : []),
          ]}
          data={data}
          pageSize={15}
        />
      )}

      <FacilityFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        initial={editing}
        onSaved={() => { setDialogOpen(false); load(); }}
      />
    </div>
  );
}
